import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import classnames from 'classnames';
import React, {useState} from 'react';
import {SemVer} from 'semver';

import notifications from 'notifications';
import {BORDER_RADIUS_DEFAULT} from 'styles/StyleConstants';
import {GRAY_LIGHT} from 'ui-library/Colors';
import FieldError from 'ui-library/FieldError';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import InputWrapper from 'ui-library/inputs/InputWrapper';
import NumericInput from 'ui-library/inputs/NumericInput';
import {assertNever, NumericInputChangeEvent} from 'util/Types';

enum SemVerField {
  MAJOR,
  MINOR,
  PATCH,
}

interface Props {
  // Currently selected input value
  versionString: string;
  // input is validated against all current versions
  currentVersions: Array<string>;
  onChange: (value: string) => void;
  onValidation: (isValid: boolean) => void;
}

export function incrementPatchVersion(versionString: string): string {
  const value = new SemVer(validateSemverString(versionString));
  value.patch += 1;
  return value.format();
}

// returns 1 (if version1 is greater), 0 (if equal), or -1 (if version2 is greater)
export function compareSemVerStrings(version1: string, version2: string): number {
  const semVer1 = new SemVer(version1);
  const semVer2 = new SemVer(version2);
  return semVer1.compare(semVer2);
}

export function biggestSoFar(currentVersions: Array<SemVer>): SemVer | null {
  if (currentVersions.length === 0) {
    return null;
  }
  // Sorted by semantic version, highest first, e.g. ['1.0.0', '0.2.0', '0.0.1]
  const sortedVersions = [...currentVersions].sort((s1, s2) => s1.compare(s2)).reverse();
  return sortedVersions[0];
}

export function biggestSoFarWithSameMajor(major: number, currentVersions: Array<SemVer>): SemVer | null {
  return biggestSoFar(currentVersions.filter((v) => v.major === major));
}

export function isNewerVersionThanOthers(version: SemVer, currentVersions: Array<SemVer>): boolean {
  const biggest = biggestSoFarWithSameMajor(version.major, currentVersions);
  return biggest == null ? true : version.compare(biggest) === 1;
}

export function validateSemverString(versionString: string): string {
  // BenchlingAppDefinitionVersion is more restrictive than regular semver.
  // It requires additional validation, e.g.
  // We require exactly three fields. We don't support prerelease field.
  const value = new SemVer(versionString);
  const splitVersion = versionString.split('.');
  if (value.prerelease.length > 0 || splitVersion.length < 1 || splitVersion.length > 3) {
    throw new TypeError(`Invalid version: "${versionString}"`);
  }
  return value.format();
}

export default function BenchlingAppVersionInput(props: Props): JSX.Element {
  try {
    const value = new SemVer(validateSemverString(props.versionString)).format();
    const newProps = {
      ...props,
      value,
    };
    return <BenchlingAppVersionInputSafe {...newProps} />;
  } catch (error) {
    if (error instanceof TypeError) {
      const errMsg = error.toString();
      notifications.error(errMsg);
      props.onValidation(false);
      return (
        <div className={css(styles.group)}>
          <FieldError message={errMsg} />
        </div>
      );
    }
    throw error;
  }
}

function BenchlingAppVersionInputSafe(props: Props): JSX.Element {
  const value = new SemVer(props.versionString);
  const [major, setMajor] = useState<number>(value.major);
  const [minor, setMinor] = useState<number>(value.minor);
  const [patch, setPatch] = useState<number>(value.patch);
  // Error message strings to display to user
  const [errors, setErrors] = useState<Array<string>>([]);
  // Individually keep track of the isValid state of the three input fields
  const [fieldsValidations, setFieldsValidations] = useState<Array<boolean>>([true, true, true]);
  const {onChange, onValidation} = props;

  function updateField(newValue: number, field: SemVerField): void {
    switch (field) {
      case SemVerField.MAJOR:
        setMajor(newValue);
        break;
      case SemVerField.MINOR:
        setMinor(newValue);
        break;
      case SemVerField.PATCH:
        setPatch(newValue);
        break;
      default:
        assert(false, `Unknown enum value: ${assertNever(field)}`);
    }
  }

  // When one of the three input fields are changed:
  //  1. We keep track of which input fields are valid (fieldsValidations)
  //     Update as needed, and record an error message to display
  //  2. We make sure new version is greater than the old version.
  function handleChangeField(event: NumericInputChangeEvent, field: SemVerField): void {
    const fieldIndexes = {
      [SemVerField.MAJOR]: 0,
      [SemVerField.MINOR]: 1,
      [SemVerField.PATCH]: 2,
    };
    const fieldNames = ['Major', 'Minor', 'Patch'];

    const proposedFieldsValid = [...fieldsValidations];
    proposedFieldsValid[fieldIndexes[field]] = event.target.isValid;
    const invalidFields = fieldNames.filter((name, i) => !proposedFieldsValid[i]);
    const newFieldValue = event.target.numericValue ?? 0;
    setFieldsValidations(proposedFieldsValid);
    if (event.target.isValid) {
      // Capture new value if it's valid, even if other fields are invalid
      updateField(newFieldValue, field);
    }
    // Invalidate component if any field is invalid
    if (invalidFields.length > 0) {
      setErrors([`Invalid values: ${invalidFields.join(', ')}`]);
      onValidation(false);
      return;
    }
    setErrors([]);

    const newObj = {
      [SemVerField.MAJOR]: major,
      [SemVerField.MINOR]: minor,
      [SemVerField.PATCH]: patch,
    };
    newObj[field] = newFieldValue;

    const newValue = new SemVer(
      `${newObj[SemVerField.MAJOR]}.${newObj[SemVerField.MINOR]}.${newObj[SemVerField.PATCH]}`
    );
    const previous = props.currentVersions.map((v) => new SemVer(v));
    const biggestSoFar = biggestSoFarWithSameMajor(newValue.major, previous);

    if (biggestSoFar != null && newValue.compare(biggestSoFar) !== 1) {
      setErrors([`New version must be greater than ${biggestSoFar}`]);
      onValidation(false);
      return;
    }
    // New value is valid
    setErrors([]);
    updateField(newFieldValue, field);
    onChange(newValue.format());
    onValidation(true);
  }

  const inputStyle = css(styles.label);
  const labelStyle = FieldLabel.STYLES.LABEL;

  return (
    <div className={css(styles.group)}>
      <InputWrapper key="major" id="major" label="Major" labelStyle={labelStyle} className={inputStyle}>
        <NumericInput
          value={major}
          required={true}
          className={css(styles.formInput)}
          min={0}
          onChange={(value) => {
            handleChangeField(value, SemVerField.MAJOR);
          }}
          validateInteger={true}
        />
      </InputWrapper>
      <InputWrapper key="minor" id="minor" label="Minor" labelStyle={labelStyle} className={inputStyle}>
        <NumericInput
          value={minor}
          required={true}
          className={css(styles.formInput)}
          min={0}
          onChange={(value) => {
            handleChangeField(value, SemVerField.MINOR);
          }}
          validateInteger={true}
        />
      </InputWrapper>
      <InputWrapper key="patch" id="patch" label="Patch" labelStyle={labelStyle} className={inputStyle}>
        <NumericInput
          value={patch}
          required={true}
          className={css(styles.formInput)}
          min={0}
          onChange={(value) => {
            handleChangeField(value, SemVerField.PATCH);
          }}
          validateInteger={true}
        />
      </InputWrapper>
      {errors.length > 0 && errors.map((msg) => <FieldError key={msg} message={msg} />)}
    </div>
  );
}

interface BenchlingAppVersionReadOnlyProps {
  versionString: string | null;
}

export function BenchlingAppVersionReadOnly(props: BenchlingAppVersionReadOnlyProps): JSX.Element {
  let major = '';
  let minor = '';
  let patch = '';
  if (props.versionString != null) {
    const value = new SemVer(props.versionString);
    major = value.major.toString();
    minor = value.minor.toString();
    patch = value.patch.toString();
  }
  const inputStyle = css(styles.label);
  const labelStyle = FieldLabel.STYLES.LABEL;
  const valueStyle = classnames('form-control', css(styles.formInput, styles.disabled, styles.value));

  // We use <div/> here (below) to simulate a disabled NumericInput.
  // We would prefer to use NumericInput instead of div inside these InputWrappers.
  // Unfortunately, NumericInput's implementation does not rerender
  // when its props.value changes, so it does not behave correctly.

  return (
    <div className={css(styles.group)}>
      <InputWrapper key="major" id="major" label="Major" labelStyle={labelStyle} className={inputStyle}>
        <div className={valueStyle}>{major}</div>
      </InputWrapper>
      <InputWrapper key="minor" id="minor" label="Minor" labelStyle={labelStyle} className={inputStyle}>
        <div className={valueStyle}>{minor}</div>
      </InputWrapper>
      <InputWrapper key="patch" id="patch" label="Patch" labelStyle={labelStyle} className={inputStyle}>
        <div className={valueStyle}>{patch}</div>
      </InputWrapper>
    </div>
  );
}

const styles = StyleSheet.create({
  group: {
    marginBottom: 15,
  },
  disabled: {
    backgroundColor: GRAY_LIGHT,
  },
  label: {
    display: 'inline-block',
    marginRight: '1em',
  },
  formInput: {
    width: '5em',
  },
  value: {
    borderRadius: BORDER_RADIUS_DEFAULT,
  },
});
